import type { OrderDetail } from '@/api/pay'

export type H5PaymentRecoveryState =
  | 'awaiting_payment'
  | 'payment_failed'
  | 'fulfillment_repairing'
  | 'fulfilled'

export interface H5PaymentRecoveryDecision {
  declareSuccess: boolean
  clearPendingOrder: boolean
  allowNewOrder: boolean
  continuePolling: boolean
}

type PendingPayChannel = 'apple_iap' | 'hosted_checkout' | string | null

export function shouldBlockNewH5Checkout(
  pendingOrderId: string | null,
  pendingPayChannel: PendingPayChannel,
): boolean {
  return Boolean(
    pendingOrderId
    && pendingPayChannel === 'hosted_checkout',
  )
}

export function shouldClearPendingPaymentContext(
  state: H5PaymentRecoveryState,
  pendingPayChannel: PendingPayChannel,
): boolean {
  if (state === 'fulfilled') return true
  return state === 'payment_failed'
    && pendingPayChannel === 'hosted_checkout'
}

export function classifyH5PaymentOrder(
  order: Pick<OrderDetail, 'status' | 'fulfillment_status'>,
): H5PaymentRecoveryState {
  if (order.status === 'failed') return 'payment_failed'
  if (order.status === 'paid' && order.fulfillment_status === 'fulfilled') {
    return 'fulfilled'
  }
  if (order.status === 'paid') return 'fulfillment_repairing'
  return 'awaiting_payment'
}

export function getH5PaymentRecoveryDecision(
  state: H5PaymentRecoveryState,
): H5PaymentRecoveryDecision {
  switch (state) {
    case 'fulfilled':
      return {
        declareSuccess: true,
        clearPendingOrder: true,
        allowNewOrder: true,
        continuePolling: false,
      }
    case 'payment_failed':
      return {
        declareSuccess: false,
        clearPendingOrder: true,
        allowNewOrder: true,
        continuePolling: false,
      }
    case 'fulfillment_repairing':
    case 'awaiting_payment':
      return {
        declareSuccess: false,
        clearPendingOrder: false,
        allowNewOrder: false,
        continuePolling: true,
      }
  }
}

interface PollH5PaymentOrderOptions {
  orderId: string
  getOrder: (orderId: string) => Promise<
    Pick<OrderDetail, 'status' | 'fulfillment_status'>
  >
  wait?: (milliseconds: number) => Promise<void>
  intervalMs?: number
  maxAttempts?: number
}

const defaultWait = (milliseconds: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, milliseconds))

export async function pollH5PaymentOrder({
  orderId,
  getOrder,
  wait = defaultWait,
  intervalMs = 3000,
  maxAttempts = 10,
}: PollH5PaymentOrderOptions): Promise<H5PaymentRecoveryState> {
  let latestState: H5PaymentRecoveryState = 'awaiting_payment'

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      const order = await getOrder(orderId)
      latestState = classifyH5PaymentOrder(order)
      const decision = getH5PaymentRecoveryDecision(latestState)
      if (!decision.continuePolling) return latestState
    } catch {
      // A transient read failure must not erase or release the pending order.
    }

    if (attempt < maxAttempts - 1) await wait(intervalMs)
  }

  return latestState
}
